import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { UserCheck, Printer, Smartphone, Gift } from 'lucide-react';
import axios from 'axios';
import { AIReferralButton } from '../referrals/AIReferralDialog';

export const ReferralsTab = ({ dashboardData, error, filteredReferrals, fmtDateUS, loadDashboard, navigate, practice, printPatientReport, safeText, selectedPatientId, selectedPatientReferralSource, showSmsSelectionModal, toast, user }) => (
  <>
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => printPatientReport({
            tabKey: 'referrals',
            reportTitle: 'Referrals Report',
            subtitle: 'Specialist referrals made for this patient + the source that referred them',
            itemNoun: 'referral',
            items: [
              ...(selectedPatientReferralSource ? [{
                __isSource: true,
                name: selectedPatientReferralSource.name,
                sourceType: selectedPatientReferralSource.sourceType,
                email: selectedPatientReferralSource.email,
                phone: selectedPatientReferralSource.phone,
                notes: selectedPatientReferralSource.notes,
              }] : []),
              ...filteredReferrals,
            ],
            headerColor: '#0d9488',
            columns: [
              { key: 'direction', label: 'Direction' },
              { key: 'who', label: 'Specialist / Source' },
              { key: 'specialty', label: 'Specialty / Type' },
              { key: 'concern', label: 'Chief Concern' },
              { key: 'contact', label: 'Contact' },
              { key: 'date', label: 'Date' },
            ],
            rowFor: (r) => {
              if (r.__isSource) {
                return [
                  '<span class="badge badge-amber">SOURCE (referred IN)</span>',
                  `<strong>${safeText(r.name)}</strong>`,
                  safeText(r.sourceType),
                  safeText(r.notes),
                  [r.email, r.phone].filter(Boolean).map(safeText).join(' · ') || '—',
                  '—',
                ];
              }
              return [
                '<span class="badge badge-green">REFERRED OUT</span>',
                `<strong>${safeText(r.specialistName || r.toName || '—')}</strong>`,
                safeText(r.specialty),
                safeText(r.chiefConcern),
                [r.specialistEmail, r.specialistPhone].filter(Boolean).map(safeText).join(' · ') || '—',
                fmtDateUS(r.createdAt || r.referralDate),
              ];
            },
            stats: (items) => [
              { label: 'Referred OUT', value: items.filter((x) => !x.__isSource).length, color: '#0d9488' },
              { label: 'Source', value: items.filter((x) => x.__isSource).length, color: '#a855f7' },
            ],
          })}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md"
          data-testid="referrals-print-report-btn"
        >
          <Printer className="h-3.5 w-3.5" />
          Print Referrals Report
        </button>
      </div>
      {/* Referral From Section - Who referred this patient to the practice */}
      {selectedPatientReferralSource && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-purple-700 mb-2 flex items-center">
            <Gift className="h-4 w-4 mr-2" />
            Referral From (Who referred this patient to you)
          </h4>
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Gift className="h-6 w-6 text-purple-600" />
                  <h4 className="font-semibold text-lg text-gray-900">
                    {selectedPatientReferralSource.name}
                  </h4>
                  <Badge className="bg-purple-100 text-purple-700">
                    {selectedPatientReferralSource.sourceType}
                  </Badge>
                </div>
                {selectedPatientReferralSource.email && (
                  <p className="text-sm text-purple-700">{selectedPatientReferralSource.email}</p>
                )}
                {selectedPatientReferralSource.phone && (
                  <p className="text-sm text-purple-700">{selectedPatientReferralSource.phone}</p>
                )}
                {selectedPatientReferralSource.notes && (
                  <p className="text-sm text-gray-600 mt-2 italic">{selectedPatientReferralSource.notes}</p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/referral-management?tab=from')}
                className="text-purple-600 border-purple-300 hover:bg-purple-100"
              >
                View Source
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Referral To Section - Specialists this patient was referred to */}
      <div>
        <h4 className="text-sm font-semibold text-teal-700 mb-2 flex items-center">
          <UserCheck className="h-4 w-4 mr-2" />
          Referral To (Specialists this patient was referred to)
        </h4>
        {filteredReferrals.length > 0 ? (
          filteredReferrals.map((referral) => (
        <div key={referral.id} className="p-4 bg-teal-50 border border-teal-200 rounded-lg mb-3">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <UserCheck className="h-6 w-6 text-teal-600" />
                <h4 className="font-semibold text-lg text-gray-900">
                  {referral.specialistName || 'Specialist Referral'}
                </h4>
                {referral.status === 'pending_ai' && (
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`referral-pending-badge-${referral.id}`}>Pending – not sent yet</Badge>
                )}
              </div>
              {referral.status === 'pending_ai' && (
                <div className="mb-2">
                  <AIReferralButton patientId={selectedPatientId} pendingReferralId={referral.id} label="Send this referral with AI"
                    initialInstruction={referral.aiInstruction || ''} onSent={() => loadDashboard(true)} className="h-8 text-sm" />
                </div>
              )}
              {referral.specialty && (
                <Badge variant="outline" className="bg-teal-100 text-teal-700 mb-2 text-sm">
                  {referral.specialty}
                </Badge>
              )}
              <p className="text-base text-gray-600 mb-1">
                {referral.referringDentist ? `Referred by: ${referral.referringDentist}` : ''}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                Created: {new Date(referral.createdAt).toLocaleDateString()}
              </p>
              {referral.reason && (
                <p className="text-base text-gray-700 bg-white p-2 rounded border border-teal-100">
                  <strong>Reason:</strong> {referral.reason}
                </p>
              )}
            </div>
            <div className="flex flex-col space-y-1">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    toast({
                      title: "Opening Referral",
                      description: "Loading referral PDF...",
                    });
                    const token = localStorage.getItem('dentalToken');
                    const response = await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/${referral.id}/generate-pdf`,
                      {},
                      {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      }
                    );
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                  } catch (error) {
                    console.error('View error:', error);
                    toast({
                      title: "View Error",
                      description: error.response?.data?.detail || "Failed to open referral",
                      variant: "destructive",
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                View
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigate(`/referral-management?referralId=${referral.id}&mode=edit`);
                }}
                className="text-sm px-3 py-1 h-8"
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    toast({
                      title: "Preparing Print",
                      description: "Loading referral PDF...",
                    });

                    const token = localStorage.getItem('dentalToken');
                    const response = await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/${referral.id}/generate-pdf`,
                      {},
                      {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      }
                    );

                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const blobUrl = URL.createObjectURL(blob);

                    const iframe = document.createElement('iframe');
                    iframe.style.position = 'fixed';
                    iframe.style.left = '-9999px';
                    iframe.style.width = '0';
                    iframe.style.height = '0';
                    iframe.style.border = 'none';
                    iframe.src = blobUrl;
                    document.body.appendChild(iframe);

                    iframe.onload = function() {
                      try {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                      } catch (e) {
                        console.error('Print error:', e);
                      }
                      // Don't remove iframe immediately - wait for user to finish printing
                      setTimeout(() => {
                        if (document.body.contains(iframe)) {
                          document.body.removeChild(iframe);
                        }
                        URL.revokeObjectURL(blobUrl);
                      }, 60000); // 60 seconds
                    };
                  } catch (error) {
                    console.error('Print error:', error);
                    toast({
                      title: "Print Failed",
                      description: "Failed to print referral",
                      variant: "destructive",
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                Print
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const patient = dashboardData?.recentPatients?.find(p => p.id === referral.patientId);
                    if (!patient?.email) {
                      toast({
                        title: "No Email Address",
                        description: "Patient email not found",
                        variant: "destructive"
                      });
                      return;
                    }

                    toast({
                      title: "Sending Email",
                      description: `Sending referral to ${patient.email}...`,
                    });

                    const token = localStorage.getItem('dentalToken');
                    await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/referrals/${referral.id}/email?send_to_specialist=false&send_to_patient=true`,
                      {},
                      {
                        headers: { Authorization: `Bearer ${token}` }
                      }
                    );

                    toast({
                      title: "✅ Email Sent Successfully",
                      description: `Referral sent to ${patient.email}`,
                    });
                    loadDashboard();
                  } catch (error) {
                    console.error('Email error:', error);
                    toast({
                      title: "Email Failed",
                      description: error.response?.data?.detail || "Failed to send email",
                      variant: "destructive",
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                Email
              </Button>
              <Button
                data-testid={`text-referral-${referral.id}`}
                size="sm"
                variant="outline"
                onClick={() => {
                  const patient = dashboardData?.recentPatients?.find(p => p.id === referral.patientId);
                  // For referrals, we might have specialist info
                  showSmsSelectionModal(
                    referral.patientId,
                    'referral',
                    referral.id,
                    patient?.cellPhone || patient?.phone,
                    `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
                    referral.specialistName || 'Referral',
                    referral.specialistPhone || null,
                    referral.specialistName || null
                  );
                }}
                className="text-sm px-3 py-1 h-8"
              >
                <Smartphone className="h-3 w-3 mr-1" />
                Text
              </Button>
            </div>
          </div>
        </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p>No outbound referrals for this patient yet</p>
            <Button
              onClick={() => navigate('/referral-management')}
              className="mt-2 bg-teal-600 hover:bg-teal-700"
              size="sm"
            >
              Create Referral
            </Button>
          </div>
        )}
      </div>
    </div>
  </>
);
